import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Alert } from "@mui/material";
import UpdateEventDetails from "./UpdateEventDetails";
import { usePermission } from "../../hooks/usePermission";
import { useGetEventAttendees } from "../../hooks/useAttendee";
import "react-toastify/dist/ReactToastify.css";
import {
  formatDate,
  formatPrice,
  formatTime,
  getCurrencyCode,
} from "../../utils/helpers";
import { EventStatusConstants } from "../../constants/eventStatus";
import { EditEventsPermission } from "../../constants/permissions";
import EventStatusBadge, { getEventStatus } from "./EventStatus";
import { useEditEvent } from "../../hooks/useEvent";
import { useNamesByIds } from "../../hooks/useNamesByIds";
import {
  ShimmerTitle,
  ShimmerBadge,
  ShimmerCategoryItem,
} from "react-shimmer-effects";
function EventDetailsInfo({ eventData, isLoading }) {
  const { eventId } = useParams();
  const [openEditModal, setOpenEditModal] = useState(false);
  const { mutate: editEvent } = useEditEvent();
  const { data: EventAttendees, isLoading: attendeesLoading } = useGetEventAttendees(
    eventData?.id ? eventId : null
  );

  const { data: canEditEvents } = usePermission({
    permissionName: EditEventsPermission,
  });

  const spendPerAttendee = useMemo(() => {
    if (!eventData?.spend_value || !EventAttendees?.length) return 0;
    const totalSpend = parseFloat(eventData.spend_value);
    const attendeeCount = EventAttendees.length;
    return formatPrice(totalSpend / attendeeCount);
  }, [eventData?.spend_value, EventAttendees?.length]);

  const { data: lookups, isLoading: lookupsLoading } = useNamesByIds(
    eventData ? eventData : null
  );

  const shouldUpdateStatus = useMemo(() => {
    if (!eventData || !EventAttendees) return false;

    const currentStatus = getEventStatus(
      eventData.start_date,
      eventData.end_date,
      EventAttendees,
      eventData.spend_value,
      eventData.organization_id,
      eventData.tov_exported
    );

    return currentStatus.text !== eventData.status;
  }, [
    eventData?.start_date,
    eventData?.end_date,
    eventData?.spend_value,
    eventData?.organization_id,
    eventData?.tov_exported,
    eventData?.status,
    EventAttendees?.length,
  ]);

  const updateEventStatus = useCallback(() => {
    if (!eventData?.id || !EventAttendees) return;

    const currentStatus = getEventStatus(
      eventData.start_date,
      eventData.end_date,
      EventAttendees,
      eventData.spend_value,
      eventData.organization_id,
      eventData.tov_exported
    );

    editEvent({ id: eventData.id, data: { status: currentStatus.text } });
  }, [eventData, EventAttendees, editEvent]);

  useEffect(() => {
    if (shouldUpdateStatus) {
      updateEventStatus();
    }
  }, [shouldUpdateStatus, updateEventStatus]);

  if (isLoading || lookupsLoading || attendeesLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-4">
        {/* Header Section with ID and Status */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center justify-center gap-4">
            <ShimmerBadge width={100} height={40} />
          </div>

          <div className="px-4 py-2">
            <ShimmerBadge width={120} height={40} />
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-6 border-b border-slate-200">
          <div className="space-y-6">
            <>
              <ShimmerTitle line={1} />
              <ShimmerCategoryItem />
              <ShimmerCategoryItem />
            </>
          </div>

          <div className="space-y-6">
            <>
              <ShimmerCategoryItem />
              <ShimmerCategoryItem />
              <ShimmerCategoryItem />
            </>
          </div>
        </div>

        {/* Financial Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-6 border-b border-slate-200">
          <>
            <div className="space-y-6">
              <ShimmerCategoryItem />
              <ShimmerCategoryItem />
              <ShimmerCategoryItem />
            </div>
            <div className="space-y-6">
              <ShimmerCategoryItem />
              <ShimmerCategoryItem />
              <ShimmerCategoryItem />
            </div>
          </>
        </div>

        {/* Additional Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8">
          <>
            <div className="space-y-6">
              <ShimmerCategoryItem />
              <ShimmerCategoryItem />
              <ShimmerCategoryItem />
            </div>
            <div className="space-y-6">
              <ShimmerCategoryItem />
              <ShimmerCategoryItem />
              <ShimmerCategoryItem />
            </div>
          </>
        </div>
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Alert severity="error">Event not found</Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-2">
      {eventData.status === EventStatusConstants.ENDED.text && (
        <Alert severity="warning" className="mb-4">
          This event has ended. Please ensure all attendees data is complete and
          spend value is recorded.
        </Alert>
      )}
      {eventData.status === EventStatusConstants.COMPLETED.text && (
        <Alert severity="info" className="mb-4">
          This event is completed and ready for TOV export.
        </Alert>
      )}
      <div className="bg-white rounded-lg shadow-md p-6 mb-4">
        {/* Header Section with ID and Status */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center justify-center gap-4">
            <div className="bg-blueColor text-white text-2xl p-3 rounded-lg font-bold shadow-sm">
              ID: {eventData.id}
            </div>

            {canEditEvents && (
              <button
                onClick={() => setOpenEditModal(true)}
                className={`bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 transition-colors duration-200 shadow-sm ${eventData.status === EventStatusConstants.CLOSED.text
                  ? "hidden"
                  : ""
                  }`}
              >
                <i className="fa-solid fa-edit"></i>
              </button>
            )}
          </div>

          <div className="px-4 py-2">
            <EventStatusBadge size="xl" status={eventData.status} />
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-6 border-b border-slate-200">
          <div className="space-y-6">
            <div>
              <p className="text-gray-600 text-sm mb-1">Event/Topic Name</p>
              <p className="font-semibold text-lg">{eventData.name}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm mb-1">Event Type</p>
              <p className="font-semibold">{lookups?.eventTypeName}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm mb-1">Country of Event</p>
              <button className="bg-blueColor text-white rounded-lg px-3 py-1.5 text-sm shadow-sm">
                {eventData.country_name}
              </button>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <p className="text-gray-600 text-sm mb-1">Event Date (Start)</p>
              <p className="font-semibold">
                {formatDate(eventData.start_date)} at{" "}
                {formatTime(eventData.start_time)}
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm mb-1">Event Date (End)</p>
              <p className="font-semibold">
                {formatDate(eventData.end_date)} at{" "}
                {formatTime(eventData.end_time)}
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm mb-1">Event City</p>
              <button className="bg-blueColor text-white rounded-lg px-3 py-1.5 text-sm shadow-sm">
                {eventData.city_name}
              </button>
            </div>
          </div>
        </div>

        {/* Financial Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-6 border-b border-slate-200">
          <div className="space-y-6">
            <div>
              <p className="text-gray-600 text-sm mb-1">Spend Type</p>
              <p className="font-semibold">{lookups?.spendTypeName}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm mb-1">Total Spend Value</p>
              <p className="font-semibold">
                {eventData.spend_value
                  ? `${formatPrice(eventData.spend_value)} ${getCurrencyCode(
                    eventData.currency
                  )}`
                  : "Not specified"}
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm mb-1">
                Spend Value per Attendee
              </p>
              <p className="font-semibold">
                {eventData.spend_value && EventAttendees?.length
                  ? `${spendPerAttendee} ${getCurrencyCode(eventData.currency)}`
                  : "Not available"}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <p className="text-gray-600 text-sm mb-1">Activity Type</p>
              <p className="font-semibold">{lookups?.activityTypeName}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm mb-1">Currency</p>
              <button className="bg-blueColor text-white rounded-lg px-3 py-1.5 text-sm shadow-sm">
                {eventData.currency}
              </button>
            </div>
            <div>
              <p className="text-gray-600 text-sm mb-1">Number of Attendees</p>
              <p className="font-semibold">{EventAttendees?.length || 0}</p>
            </div>
          </div>
        </div>

        {/* Additional Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8">
          <div className="space-y-6">
            {lookups?.organizationName && (
              <div>
                <p className="text-gray-600 text-sm mb-1">Organization</p>
                <p className="font-semibold">{lookups?.organizationName}</p>
              </div>
            )}
            <div>
              <p className="text-gray-600 text-sm mb-1">Division</p>
              <p className="font-semibold">{lookups?.divisionName}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm mb-1">Team</p>
              <p className="font-semibold">{lookups?.teamName}</p>
            </div>
            {
              lookups?.brandName && (
                <div>
                  <p className="text-gray-600 text-sm mb-1">Brand</p>
                  <p className="font-semibold">{lookups?.brandName}</p>
                </div>
              )
            }
            <div>
              <p className="text-gray-600 text-sm mb-1">
                Promo/GPAS Number/UPI
              </p>
              <p className="font-semibold">{eventData.promo_number}</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <p className="text-gray-600 text-sm mb-1">
                Promo/GPAS Approval Date
              </p>
              <p className="font-semibold">
                {formatDate(eventData.promo_approval_date)}
              </p>
            </div>
            <div>
              <p className="text-gray-600">
                Attached Approved Promo/GPAS document:
              </p>

              <p className="font-semibold">
                <a
                  href={eventData.attached_files}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blueColor hover:underline"
                >
                  View File
                </a>
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm mb-1">
                Relationship of this recipient:
              </p>
              <p className="font-semibold text-lg">
                {lookups?.relationshipName}
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-sm mb-1">
                External Source System/Process
              </p>
              <p className="font-semibold">{lookups?.externalSourceName}</p>
            </div>
          </div>
        </div>
      </div>
      <UpdateEventDetails
        openEditModal={openEditModal}
        setOpenEditModal={setOpenEditModal}
        eventData={eventData}
      />
    </div>
  );
}
export default EventDetailsInfo; 
